/**
 * Shared select-option resolution for Admin modal + inline editors.
 * Keeps dropdown fields consistent between EntityForm and InlineFieldEditor.
 */

const optionValue = (item, nameKeys) => {
  for (const key of nameKeys) {
    const val = item?.[key];
    if (val != null && String(val).trim() !== '') {
      return String(val);
    }
  }
  return null;
};

const dedupeOptions = (options) => {
  const seen = new Set();
  return options.filter((opt) => {
    const key = String(opt.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const withCurrentValue = (options, currentValue) => {
  if (currentValue == null || currentValue === '') return options;
  const str = String(currentValue);
  if (options.some((opt) => String(opt.value) === str)) return options;
  return [{ value: str, label: str }, ...options];
};

const stringsToOptions = (values) =>
  (values || [])
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => {
      const str = String(v);
      return { value: str, label: str };
    });

/**
 * @param {object} field - field def from adminEntities
 * @param {{ departments?: array, roles?: array, designations?: string[], treatmentTypes?: string[], currentValue?: any }} context
 */
export const getFieldSelectOptions = (field, context = {}) => {
  if (!field || field.type !== 'select') return [];

  const {
    departments = [],
    roles = [],
    designations = [],
    treatmentTypes = [],
    currentValue,
  } = context;
  let options = [];

  if (field.options?.length > 0) {
    options = field.options.map((opt) => ({
      value: opt.value,
      label: opt.label ?? opt.value,
    }));
  } else if (field.name === 'role' && roles.length > 0) {
    options = roles
      .map((r) => {
        const value = optionValue(r, ['roleName', 'name', 'role', 'RoleName']);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  } else if (field.name === 'department' && departments.length > 0) {
    options = departments
      .map((d) => {
        const value = optionValue(d, ['departmentName', 'name', 'department', 'DepartmentName']);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  } else if (field.name === 'designation') {
    options = stringsToOptions(designations);
  } else if (field.name === 'type') {
    options = stringsToOptions(treatmentTypes);
  }

  return withCurrentValue(dedupeOptions(options), currentValue);
};

export const isSelectField = (field) => field?.type === 'select';
